class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onResultClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  onResultClick(event) {
    const link = event.target.closest('a');

    if (!link) return;

    const root = Shopify.routes.root;

    if (root !== '/' && link.pathname.startsWith('/products')) {
      event.preventDefault();

      window.location.href = root.replace(/\/$/, '') + link.pathname + link.search;
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.localizeSearchResults(this.cachedResults[queryKey]).then((localizedMarkup) => {
      this.renderSearchResults(localizedMarkup);
    });
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const predictiveSearchUrl = routes.predictive_search_url.replace(/^\/(fa|ar)/, '');

    fetch(`${predictiveSearchUrl}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,article&resources[options][fields]=title,body&resources[limit]=5&section_id=br-predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-br-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        
        // this.renderSearchResults(resultsMarkup);
        this.localizeSearchResults(resultsMarkup).then((localizedMarkup) => {
        this.renderSearchResults(localizedMarkup);
      });

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  async localizeSearchResults(resultsMarkup) {
    const locale = document.documentElement.lang?.split('-')[0];

    const searchTranslations = {
      fa: {
        products: 'محصولات',
        articles: 'مقالات',
        searchFor: 'جستجو برای',
        noResults: 'برای "{{ terms }}" نتیجه‌ای پیدا نشد.',
        results: '{{ count }} نتیجه',
        productsCount: '{{ count }} محصول',
        articlesCount: '{{ count }} مقاله',
        GBP: 'پوند',
        EUR: 'یورو',
      },
      ar: {
        products: 'المنتجات',
        articles: 'المقالات',
        searchFor: 'البحث عن',
        noResults: 'لم يتم العثور على نتائج لـ "{{ terms }}".',
        results: '{{ count }} نتيجة',
        productsCount: '{{ count }} منتجات',
        articlesCount: '{{ count }} مقالات',
        GBP: 'باوند',
        EUR: 'يورو',
      },
    };

    if (!['fa', 'ar'].includes(locale)) return resultsMarkup;

    const container = document.createElement('div');
    container.innerHTML = resultsMarkup;

    const searchLabels = searchTranslations[locale];

    const countLabels = {
      results: searchLabels.results,
      products: searchLabels.productsCount,
      articles: searchLabels.articlesCount,
    };

    const liveRegion = container.querySelector('[data-predictive-search-live-region-count-value]');

    const totalResults = liveRegion?.dataset.totalResults || 0;
    const productsCount = liveRegion?.dataset.productsCount || 0;
    const articlesCount = liveRegion?.dataset.articlesCount || 0;

    if (liveRegion && countLabels.results) {
      let liveText = countLabels.results.replace('{{ count }}', totalResults);

      if (countLabels.products && Number(productsCount) > 0) {
        liveText += `: ${countLabels.products.replace('{{ count }}', productsCount)}`;
      }

      if (countLabels.articles && Number(articlesCount) > 0) {
        liveText += `, ${countLabels.articles.replace('{{ count }}', articlesCount)}`;
      }

      liveRegion.textContent = liveText;
    }

    const productsLabel = container.querySelector('#predictive-search-products span');

    if (productsLabel && searchLabels.products) {
      productsLabel.textContent = searchLabels.products;
    }

    const articlesLabel = container.querySelector('#predictive-search-articles span');

    if (articlesLabel && searchLabels.articles) {
      articlesLabel.textContent = searchLabels.articles;
    }

    const searchForLabel = container.querySelector('[data-predictive-search-search-for-text]');

    if (searchForLabel && searchLabels.searchFor) {
      searchForLabel.textContent = `${searchLabels.searchFor} "${this.searchTerm}"`;
    }

    const noResults = container.querySelector('.text-sm.text-muted-foreground');

    if (noResults && searchLabels.noResults && noResults.textContent.includes('No results found')) {
      noResults.textContent = searchLabels.noResults.replace('{{ terms }}', this.searchTerm);
    }

    container.querySelectorAll('#predictive-search-results-products-list .price-item').forEach((price) => {
      price.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = node.textContent
            .replace(/\bGBP\b/g, searchLabels.GBP)
            .replace(/\bEUR\b/g, searchLabels.EUR);
        }
      });
    });
    // Product localization
    const productLinks = container.querySelectorAll('.predictive-search__item--link-with-thumbnail');

    for (const productLink of productLinks) {
      const handle = productLink.pathname.split('/products/').pop();

      const response = await fetch(`${Shopify.routes.root}products/${handle}.js`);
      const product = await response.json();

      const title = productLink.querySelector('.predictive-search__item-heading');

      if (title) title.textContent = product.title;

      productLink.href = product.url;
    }

    // Article localization
    const articleLinks = container.querySelectorAll('#predictive-search-results-articles-list a');

    for (const link of articleLinks) {
      const articleResponse = await fetch(`${Shopify.routes.root}${new URL(link.href).pathname.replace(/^\/(fa|ar)\//, '')}`);
      const articleHTML = await articleResponse.text();

      const articleDoc = new DOMParser().parseFromString(articleHTML, 'text/html');

      const articleTitle = articleDoc.querySelector('meta[property="og:title"]')?.content;

      const title = link.querySelector('.predictive-search__item-heading');

      if (title && articleTitle) {
        title.textContent = articleTitle;
      }
      link.href = `${Shopify.routes.root}${new URL(link.href).pathname.replace(/^\/(fa|ar)\//, '').replace(/^\//, '')}`;
    }

    return container.innerHTML;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
